import { useState } from "react";
import { Box, Stack, Typography, Button, IconButton, useTheme, Popover, Chip } from "@mui/material";
import { colors } from "../source/source.styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { enIN } from "date-fns/locale";

const DSPHeader = ({ onExport, onDateRangeChange, dateRange, sourceName, isService = false, onBack }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const primaryDarkColor = theme.palette.primary.dark;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleDateClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const formatDateRange = () => {
    if (!dateRange?.startDate || !dateRange?.endDate) {
      return "Select Date Range";
    }
    const start = dayjs(dateRange.startDate).format("MMM D, YYYY");
    const end = dayjs(dateRange.endDate).format("MMM D, YYYY");
    return `${start} - ${end}`;
  };

  const selection = {
    startDate: dateRange?.startDate || new Date(),
    endDate: dateRange?.endDate || new Date(),
    key: "selection",
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate("/reports");
    }
  };

  // Format source name for display
  const formatSourceName = (name) => {
    if (!name) return "";
    return name
      .split(/[_\s-]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const displaySourceName = formatSourceName(sourceName);

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderBottom: `1px solid ${colors.border}`,
        px: 4,
        py: 2,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        {/* Left: Back button + Title */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            onClick={handleBack}
            sx={{
              color: colors.textMuted,
              "&:hover": {
                bgcolor: colors.bgPrimary,
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Stack spacing={0.5} flex={1}>
            {/* Breadcrumb */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                component="span"
                onClick={() => navigate("/reports")}
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: colors.textMuted,
                  cursor: "pointer",
                  "&:hover": { color: theme.palette.primary.main },
                }}
              >
                REPORTS
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: colors.textMuted,
                }}
              >
                &gt;
              </Typography>
              <Typography
                component="span"
                onClick={handleBack}
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: colors.textMuted,
                  cursor: "pointer",
                  "&:hover": { color: theme.palette.primary.main },
                }}
              >
                {isService ? "SERVICES" : "SOURCE"}
              </Typography>
              {sourceName && (
                <>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: colors.textMuted,
                    }}
                  >
                    &gt;
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {displaySourceName.toUpperCase()}
                  </Typography>
                </>
              )}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: colors.textPrimary,
                }}
              >
                {sourceName ? `${displaySourceName} Performance` : isService ? "Detailed Service Performance" : "Detailed Source Performance"}
              </Typography>
              {sourceName && (
                <Chip
                  label={isService ? "Service Filter Applied" : "Source Filter Applied"}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: 11,
                    fontWeight: 600,
                    bgcolor: `${primaryColor}15`,
                    color: primaryColor,
                    borderRadius: 2,
                  }}
                />
              )}
            </Stack>
          </Stack>
        </Stack>

        {/* Right: Date Range + Export */}
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={handleDateClick}
            sx={{
              px: 2,
              py: 0.75,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              fontSize: 13,
              borderColor: open ? primaryColor : colors.border,
              color: open ? primaryColor : colors.textSecondary,
              "&:hover": {
                borderColor: primaryColor,
                bgcolor: colors.bgPrimary,
              },
            }}
          >
            {formatDateRange()}
          </Button>

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 3,
                boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
              },
            }}
          >
            <DateRange
              ranges={[selection]}
              locale={enIN}
              moveRangeOnFirstSelection={false}
              showMonthAndYearPickers={true}
              rangeColors={[primaryColor]}
              onChange={({ selection }) => {
                let startDate = selection.startDate;
                let endDate = selection.endDate || selection.startDate;

                // If single date selected (same day), set endDate to end of that day
                if (startDate.toDateString() === endDate.toDateString()) {
                  startDate = new Date(startDate.setHours(0, 0, 0, 0));
                  endDate = new Date(endDate);
                  endDate.setHours(23, 59, 59, 999);
                }

                onDateRangeChange?.({
                  startDate,
                  endDate,
                });
              }}
            />
          </Popover>

          <Button
            variant="contained"
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={onExport}
            sx={{
              px: 2.5,
              py: 0.75,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              bgcolor: primaryColor,
              color: "#FFFFFF",
              boxShadow: "none",
              "&:hover": {
                bgcolor: primaryDarkColor,
                boxShadow: "none",
              },
            }}
          >
            Export PDF
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default DSPHeader;
